import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { MessageSquare, ThumbsUp, ThumbsDown } from "lucide-react";
import { format } from "date-fns";
import { SAGE } from "@shared/brand";

// Admin review of F&F feedback (s102 DB-16 1d). administrator-gated by the server route
// (GET /api/admin/feedback). One feed of both kinds: widget submissions (type + body +
// screenshot) and passive thumbs (section + sentiment), newest first, plus a
// per-section thumbs sentiment roll-up at the top.

interface FeedbackRow {
  id: string;
  userId: string;
  kind: "widget" | "thumbs";
  type: string | null;
  body: string | null;
  sectionSlug: string | null;
  sentiment: "up" | "down" | null;
  pageUrl: string | null;
  screenshot: string | null;
  userAgent: string | null;
  createdAt: string;
  userEmail: string | null;
}

type KindFilter = "all" | "widget" | "thumbs";

const TYPE_COLOR: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
  bug: "destructive",
  confusing: "secondary",
  suggestion: "default",
  other: "outline",
};

export default function AdminFeedback() {
  const [kindFilter, setKindFilter] = useState<KindFilter>("all");

  const { data: rows = [], isLoading } = useQuery<FeedbackRow[]>({
    queryKey: ["/api/admin/feedback"],
    queryFn: async () => {
      const res = await fetch("/api/admin/feedback");
      if (!res.ok) throw new Error("Failed to fetch feedback");
      return res.json();
    },
  });

  // Per-section thumbs roll-up (e.g. "Bank Accounts: 6 👎 · 2 👍").
  const thumbsBySection = useMemo(() => {
    const map = new Map<string, { up: number; down: number }>();
    for (const r of rows) {
      if (r.kind !== "thumbs" || !r.sectionSlug) continue;
      const e = map.get(r.sectionSlug) ?? { up: 0, down: 0 };
      if (r.sentiment === "up") e.up++;
      else if (r.sentiment === "down") e.down++;
      map.set(r.sectionSlug, e);
    }
    return Array.from(map.entries())
      .map(([slug, c]) => ({ slug, ...c }))
      .sort((a, b) => b.down - a.down || b.up - a.up); // most-confusing first
  }, [rows]);

  const filtered = useMemo(
    () => (kindFilter === "all" ? rows : rows.filter((r) => r.kind === kindFilter)),
    [rows, kindFilter],
  );

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold" data-testid="text-page-title">Feedback</h1>
        <p className="text-muted-foreground mt-2">F&amp;F feedback — widget submissions and passive section thumbs.</p>
      </div>

      {/* Per-section thumbs roll-up */}
      <Card>
        <CardHeader><CardTitle>Section clarity (thumbs)</CardTitle></CardHeader>
        <CardContent>
          {thumbsBySection.length === 0 ? (
            <p className="text-sm text-muted-foreground">No thumbs yet.</p>
          ) : (
            <div className="flex flex-wrap gap-2" data-testid="thumbs-aggregate">
              {thumbsBySection.map((s) => (
                <div key={s.slug} className="flex items-center gap-2 rounded-lg border px-3 py-1.5 text-sm" data-testid={`thumbs-agg-${s.slug}`}>
                  <span className="font-mono text-xs">{s.slug}</span>
                  <span className="flex items-center gap-1 text-destructive"><ThumbsDown className="h-3.5 w-3.5" />{s.down}</span>
                  <span className="flex items-center gap-1" style={{ color: SAGE[600] }}><ThumbsUp className="h-3.5 w-3.5" />{s.up}</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-3">
          <CardTitle>All feedback ({filtered.length})</CardTitle>
          <div className="flex items-center gap-1">
            {(["all", "widget", "thumbs"] as KindFilter[]).map((k) => (
              <Button
                key={k}
                variant={kindFilter === k ? "default" : "outline"}
                size="sm"
                onClick={() => setKindFilter(k)}
                data-testid={`filter-kind-${k}`}
              >
                {k === "all" ? "All" : k === "widget" ? "Widget" : "Thumbs"}
              </Button>
            ))}
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-8 text-muted-foreground">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-8">
              <div className="flex justify-center mb-3"><div className="rounded-full bg-muted p-3"><MessageSquare className="h-6 w-6 text-muted-foreground" /></div></div>
              <p className="font-medium">No feedback yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filtered.map((r) => (
                <div key={r.id} className="rounded-lg border p-3 space-y-2" data-testid={`feedback-row-${r.id}`}>
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-2">
                      {r.kind === "widget" ? (
                        <Badge variant={TYPE_COLOR[r.type ?? "other"] ?? "outline"}>{r.type ?? "feedback"}</Badge>
                      ) : (
                        <Badge variant={r.sentiment === "down" ? "destructive" : "default"} className="flex items-center gap-1">
                          {r.sentiment === "down" ? <ThumbsDown className="h-3 w-3" /> : <ThumbsUp className="h-3 w-3" />}
                          {r.sectionSlug}
                        </Badge>
                      )}
                      <span className="text-xs text-muted-foreground">{r.userEmail ?? r.userId?.slice(0, 8)}</span>
                    </div>
                    <span className="font-mono text-xs text-muted-foreground">{format(new Date(r.createdAt), "yyyy-MM-dd HH:mm")}</span>
                  </div>

                  {r.kind === "widget" && r.body && (
                    <p className="text-sm whitespace-pre-wrap">{r.body}</p>
                  )}
                  {r.kind === "widget" && r.screenshot && (
                    <img src={r.screenshot} alt="screenshot" className="max-h-48 rounded border" data-testid={`feedback-shot-${r.id}`} />
                  )}
                  {r.pageUrl && (
                    <a href={r.pageUrl} target="_blank" rel="noreferrer" className="block text-xs text-muted-foreground truncate hover:underline">{r.pageUrl}</a>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
